import { useRef } from 'react';
import { Button, Dropdown, Form, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useModal } from 'context/ModalContext';
import { getButtonsNoYesDanger } from 'context/helpers/helperModalContext';
import SvgCornerUpLeft from 'components/svg/SvgCornerUpLeft';
import SolarIcon from 'components/icons/SolarIcon';
import EToolTip from 'components/others/EToolTip';
import OffUploadDocument from '../../../OffUploadDocument';
import useCheckedRowsMenu, {
    CHECKED_ROWS_COMMENT_MAX_LENGTH,
    type CheckedRowsMenuProps,
} from './model/useCheckedRowsMenu';

export type { CheckedRowsKind, CheckedRowsMenuProps } from './model/useCheckedRowsMenu';

const confirmCheckedRows = (
    showDlg: (params: unknown) => void,
    buttons: Array<{ handleClick?: () => void }>,
    title: string,
    detail: string,
    onConfirm: () => void,
) => {
    const yesButton = buttons[1];
    if (yesButton) {
        yesButton.handleClick = () => {
            showDlg(false);
            onConfirm();
        };
    }
    showDlg({
        type: 'confirm',
        buttons,
        text: (
            <>
                <h4 className="d-flex text-danger">
                    <SvgCornerUpLeft className="text-danger" />
                    <span className="ps-2">{title}</span>
                </h4>
                <hr />
                <span>{detail}</span>
            </>
        ),
    });
};

const CheckedRowsMenu = ({ kind, checkedIds, setCheckedIds, setBusy }: CheckedRowsMenuProps) => {
    const { t } = useTranslation(['Objects', 'common']);
    const { showDlg } = useModal();
    const refText = useRef<HTMLTextAreaElement>(null);
    const {
        objectId,
        showText,
        setShowText,
        paymentText,
        setPaymentText,
        uploadOpen,
        setUploadOpen,
        uploadSeed,
        cancellableCount,
        handleCancel,
        openPaymentDialog,
        handleCreateDocument,
    } = useCheckedRowsMenu({ kind, checkedIds, setCheckedIds, setBusy });

    if (!checkedIds.length) return null;

    const focusPaymentText = () => {
        const el = refText.current;
        if (!el) return;
        el.focus();
        el.select();
    };

    return (
        <>
            <OffUploadDocument
                show={uploadOpen}
                setShow={setUploadOpen}
                objectId={objectId}
                initialValues={uploadSeed}
            />

            <Modal
                show={showText}
                centered
                onHide={() => setShowText(false)}
                onEntered={focusPaymentText}
            >
                <Modal.Header closeButton>
                    <Modal.Title>{t('checkedRows.createDocument')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Control
                        ref={refText}
                        as="textarea"
                        rows={5}
                        maxLength={CHECKED_ROWS_COMMENT_MAX_LENGTH}
                        value={paymentText}
                        onChange={(e) => setPaymentText(e.target.value)}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowText(false)}>
                        {t('common:buttons.close')}
                    </Button>
                    <Button variant="primary" onClick={handleCreateDocument}>
                        {t('checkedRows.btnCreate')}
                    </Button>
                </Modal.Footer>
            </Modal>

            <div className="mb-2">
                <EToolTip title={t('checkedRows.menu')}>
                    <Dropdown align="end">
                        <Dropdown.Toggle
                            className="py-2 px-2 d-inline-flex align-items-center"
                            size="sm"
                            variant="light-secondary"
                            aria-label={t('checkedRows.menu')}
                        >
                            <SolarIcon name="three-dots" />
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="p-2 shadow-sm warehouse-team-menu text-start">
                            <Dropdown.Item
                                as="button"
                                disabled={!cancellableCount}
                                onClick={(e) => {
                                    confirmCheckedRows(
                                        showDlg,
                                        getButtonsNoYesDanger(t),
                                        t('checkedRows.cancelConfirm'),
                                        t('checkedRows.cancelConfirmInfo', { count: cancellableCount }),
                                        () => {
                                            void handleCancel();
                                        },
                                    );
                                    e.currentTarget.blur();
                                }}
                            >
                                <div className="d-flex align-items-start w-100 text-start">
                                    <span className="d-inline-flex align-items-center justify-content-center flex-shrink-0 me-2 item-icon">
                                        <SvgCornerUpLeft />
                                    </span>
                                    <div className="flex-grow-1 min-w-0">
                                        <div className="fw-medium">{t('checkedRows.cancel')}</div>
                                        <small className="text-muted warehouse-team-menu-hint">
                                            {t('checkedRows.cancelHint')}
                                        </small>
                                    </div>
                                </div>
                            </Dropdown.Item>
                            <Dropdown.Item
                                as="button"
                                onClick={(e) => {
                                    openPaymentDialog();
                                    e.currentTarget.blur();
                                }}
                            >
                                <div className="d-flex align-items-start w-100 text-start">
                                    <span className="d-inline-flex align-items-center justify-content-center flex-shrink-0 me-2 item-icon">
                                        <SolarIcon name="file-earmark-text" />
                                    </span>
                                    <div className="flex-grow-1 min-w-0">
                                        <div className="fw-medium">{t('checkedRows.createDocument')}</div>
                                        <small className="text-muted warehouse-team-menu-hint">
                                            {t('checkedRows.createDocumentHint')}
                                        </small>
                                    </div>
                                </div>
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </EToolTip>
            </div>
        </>
    );
};

export default CheckedRowsMenu;
